/**
 * Signup_Validations.ts
 * Centralized validation logic for the signup form.
 */

import { REGEX } from "../utils/regex";
import { ERROR_MESSAGES } from "./commonMessages";
import { CountryEntry } from "../components/mobileNumberInput";

/* ---------------------------------------------------------
   Atomic Validators (Boolean)
--------------------------------------------------------- */

export const isPasswordStrong = (pass: string) => {
  return pass.length >= 8 &&
    REGEX.LOWERCASE.test(pass) &&
    REGEX.CAPITAL.test(pass) &&
    REGEX.NUMBER.test(pass) &&
    REGEX.SPECIAL.test(pass);
};

export const isNameValid = (name: string) => REGEX.NAME.test(name);

export const isBusinessNameValid = (name: string) =>
  name.trim().length >= 2 && name.trim().length <= 120;

/* ---------------------------------------------------------
   UI Error Resolvers (Strings)
--------------------------------------------------------- */

export const getFirstNameError = (firstName: string, touched: boolean) => {
  if (!touched) return undefined;
  if (!firstName.trim()) return "First name is required";
  if (!isNameValid(firstName)) return "Only alphabets and spaces allowed";
  return undefined;
};

export const getLastNameError = (lastName: string, touched: boolean) => {
  if (!touched) return undefined;
  if (!lastName.trim()) return "Last name is required";
  if (!isNameValid(lastName)) return "Only alphabets and spaces allowed";
  return undefined;
};

// Allowed: letters, numbers, spaces and the punctuation real company names use
// on a trade licence — _ - ( ) & . , ' /  e.g. "Urban Way Travel & Tourism L.L.C".
export const BUSINESS_NAME_ALLOWED = /^[A-Za-z0-9 _()&.,'\/-]+$/;
// The same set, negated — the sign-up and profile inputs strip these as typed.
export const BUSINESS_NAME_DISALLOWED = /[^A-Za-z0-9 _()&.,'\/-]/g;

export const getBusinessNameError = (businessName: string, touched: boolean) => {
  if (!touched) return undefined;
  if (!businessName.trim()) return ERROR_MESSAGES.BUSINESS_NAME_REQUIRED;
  if (businessName.trim().length < 2) return ERROR_MESSAGES.BUSINESS_NAME_TOO_SHORT;
  if (businessName.trim().length > 120) return ERROR_MESSAGES.BUSINESS_NAME_TOO_LONG;
  if (!BUSINESS_NAME_ALLOWED.test(businessName))
    return "Only letters, numbers, spaces and _ - ( ) & . , ' / are allowed";
  return undefined;
};

export const getCountryError = (country: string, touched: boolean) => {
  if (!touched) return undefined;
  if (!country.trim()) return ERROR_MESSAGES.COUNTRY_REQUIRED;
  return undefined;
};

export const getPasswordError = (password: string, touched: boolean) => {
  if (!touched) return undefined;
  if (!password) return ERROR_MESSAGES.PASSWORD_REQUIRED;
  if (!isPasswordStrong(password)) return ERROR_MESSAGES.PASSWORD_WEAK;
  return undefined;
};

export const getMobileError = (mobile: string, touched: boolean, selectedCountry: CountryEntry) => {
  if (!touched) return undefined;
  if (!mobile) return ERROR_MESSAGES.PHONE_REQUIRED;
  const { digitCount, name } = selectedCountry;
  if (mobile.length !== digitCount)
    return ERROR_MESSAGES.PHONE_INVALID ? (ERROR_MESSAGES.PHONE_INVALID as any)(digitCount) : `${name} needs ${digitCount} digits`;
  return undefined;
};

export const getConfirmPasswordError = (
  password: string,
  confirmPassword: string,
  touched: boolean
) => {
  if (!touched) return undefined;
  if (!confirmPassword) return "Please confirm your password";
  if (confirmPassword !== password) return "Passwords don't match";
  return undefined;
};

export const getEmailError = (email: string, isEmailValid: boolean, touched: boolean) => {
  if (!touched) return undefined;
  if (!email.trim()) return "Email is required";
  if (!isEmailValid) return "Please enter a valid email address";
  return undefined;
};

export const isFormValid = (
  form: {
    firstName:    string;
    lastName:     string;
    businessName: string;
    country:      string;
    password:     string;
    mobile:       string;
    selectedCountry: CountryEntry;
  },
  isEmailValid: boolean
) => {
  return (
    !!form.firstName.trim() && isNameValid(form.firstName) &&
    !!form.lastName.trim() && isNameValid(form.lastName) &&
    isBusinessNameValid(form.businessName) &&
    !!form.country.trim() &&
    isEmailValid &&
    isPasswordStrong(form.password) &&
    form.mobile.length === form.selectedCountry.digitCount
  );
};

/* ---------------------------------------------------------
   Aggregate validators.

   NOTE ON "STEP": signup is now ONE form, and the screen composes the
   atomic resolvers above in field order to find its first error — see
   `formError` in screens/auth/signup.tsx. The step-split aggregates below
   are kept because they still describe the two halves of the payload
   (credentials vs profile) and are useful to anything validating one half
   alone; they are not what the signup screen calls.

   Each returns the FIRST failing rule's message, or null when the fields it
   covers are fully valid.
--------------------------------------------------------- */

export interface SignupFormValues {
  firstName:       string;
  lastName:        string;
  businessName:    string;
  email:           string;
  password:        string;
  confirmPassword: string;
  mobile:          string;
  selectedCountry: CountryEntry;
}

const firstDefined = (errs: (string | undefined)[]): string | null =>
  errs.find((e) => !!e) ?? null;

/** Step 1 — account credentials (email + password + confirm). */
export const getSignupStep1Error = (
  form: SignupFormValues,
  isEmailValid: boolean
): string | null =>
  firstDefined([
    getEmailError(form.email, isEmailValid, true),
    getPasswordError(form.password, true),
    getConfirmPasswordError(form.password, form.confirmPassword, true),
  ]);

/** Step 2 — profile (name + business + mobile). */
export const getSignupStep2Error = (form: SignupFormValues): string | null =>
  firstDefined([
    getFirstNameError(form.firstName, true),
    getLastNameError(form.lastName, true),
    getBusinessNameError(form.businessName, true),
    getMobileError(form.mobile, true, form.selectedCountry),
  ]);

/** Whole form — used to gate the final submit (server never sees a bad payload). */
export const getSignupError = (
  form: SignupFormValues,
  isEmailValid: boolean
): string | null => getSignupStep1Error(form, isEmailValid) || getSignupStep2Error(form);
